import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Form, Row, Col, Badge } from "react-bootstrap";
import { getToken } from "../../utilities/authHelper";
import { fetchDashboards } from "../../utilities/AdminUtilities";

function DashboardDetails() {
  const [dashboards, setDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editable, setEditable] = useState(false);
  const [editedDashboard, setEditedDashboard] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isCreatingNewDashboard, setIsCreatingNewDashboard] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    fetchDashboards(setDashboards);
  }, []);

  const handleDashboardClick = (dashboard) => {
    setSelectedDashboard(dashboard);
    setEditedDashboard({ ...dashboard });
    setShowModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditedDashboard({ ...editedDashboard, [name]: value });
  };

  const handleDetailsChange = (e) => {
    setEditedDashboard({
      ...editedDashboard,
      details_object: e.target.value,
    });
  };

  const handleSave = async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      let detailsObject = editedDashboard.details_object;
      if (typeof detailsObject === "string") {
        try {
          detailsObject = JSON.parse(detailsObject);
        } catch (error) {
          console.error("Invalid JSON string in details_object:", error);
          detailsObject = {};
        }
      }

      const updatedDashboard = {
        ...editedDashboard,
        details_object: detailsObject,
      };

      const response = await fetch(
        `${apiUrl}/admin/create_or_update_dashboard${
          isCreatingNewDashboard ? "" : `?dashboard_id=${selectedDashboard.id}`
        }`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
          body: JSON.stringify(updatedDashboard),
        }
      );

      if (!response.ok) throw new Error("Failed to save dashboard");

      setShowSuccessModal(true);
      handleCloseModal();
      fetchDashboards(setDashboards);
    } catch (error) {
      console.error("Error saving dashboard:", error);
    }
  };

  const confirmDelete = () => setShowDeleteConfirm(true);

  const handleDelete = async () => {
    const apiUrl = process.env.REACT_APP_API_URL;

    try {
      const response = await fetch(
        `${apiUrl}/admin/delete_dashboard?dashboard_id=${selectedDashboard.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to delete dashboard");
      setShowDeleteConfirm(false);
      setShowSuccessModal(true);
      handleCloseModal();
      fetchDashboards(setDashboards);
    } catch (error) {
      console.error("Error deleting dashboard:", error);
    }
  };

  const handleCreateNewDashboard = () => {
    setIsCreatingNewDashboard(true);
    setEditedDashboard({});
    setShowModal(true);
    setEditable(true);
  };

  const handleCloseModal = () => {
    setEditable(false);
    setShowModal(false);
    setIsCreatingNewDashboard(false);
  };

  const handleCloseSuccessModal = () => setShowSuccessModal(false);

  // details_object may arrive as an object; show it as JSON text so it can be
  // edited (handleSave parses it back).
  const detailsText =
    editedDashboard.details_object && typeof editedDashboard.details_object === "object"
      ? JSON.stringify(editedDashboard.details_object, null, 2)
      : editedDashboard.details_object || "";

  const handleCancelEdit = () => {
    if (isCreatingNewDashboard) {
      handleCloseModal();
    } else {
      setEditedDashboard({ ...selectedDashboard });
      setEditable(false);
    }
  };

  const statusBadge = (status) => {
    if (!status) return "—";
    const s = String(status).toUpperCase();
    return (
      <Badge bg={s === "ACTIVE" ? "success" : s === "INACTIVE" ? "danger" : "secondary"} pill>
        {status}
      </Badge>
    );
  };

  const textField = (label, name, props = {}) => (
    <Form.Group className="ep-group">
      <Form.Label className="pv-label">{label}</Form.Label>
      <Form.Control
        type="text"
        name={name}
        value={editedDashboard[name] || ""}
        onChange={handleEditChange}
        disabled={!editable}
        {...props}
      />
    </Form.Group>
  );

  return (
    <>
      <div className="up-toolbar">
        <div className="text-muted small">
          {dashboards.length} dashboard{dashboards.length === 1 ? "" : "s"}
        </div>
        <Button variant="primary" onClick={handleCreateNewDashboard}>
          + Create Dashboard
        </Button>
      </div>

      <div className="up-card">
        <Table hover responsive className="up-table">
          <thead>
            <tr>
              <th>Dashboard Name</th>
              <th>Category</th>
              <th>Status</th>
              <th>Data Reference</th>
            </tr>
          </thead>
          <tbody>
            {dashboards.length === 0 ? (
              <tr>
                <td colSpan="4" className="up-empty">
                  No dashboards found.
                </td>
              </tr>
            ) : (
              dashboards.map((dashboard) => (
                <tr
                  key={dashboard.id}
                  className="up-row"
                  onClick={() => handleDashboardClick(dashboard)}
                >
                  <td className="up-name">{dashboard.dashboard_name || "—"}</td>
                  <td>{dashboard.dashboard_category || "—"}</td>
                  <td>{statusBadge(dashboard.dashboard_status)}</td>
                  <td className="up-email pv-mono">
                    {dashboard.data_reference || "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Main Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton className="pv-header">
          <div className="w-100">
            <div className="pv-eyebrow">
              {isCreatingNewDashboard
                ? "New Dashboard"
                : editable
                ? "Editing Dashboard"
                : "Dashboard"}
            </div>
            <div className="pv-title up-title text-break">
              {isCreatingNewDashboard
                ? editedDashboard.dashboard_name || "Untitled dashboard"
                : selectedDashboard?.dashboard_name}
            </div>
            {!isCreatingNewDashboard && selectedDashboard?.dashboard_status && (
              <div className="pv-active-line">
                <span>Status</span>
                {statusBadge(selectedDashboard.dashboard_status)}
              </div>
            )}
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => e.preventDefault()}>
            <div className="pv-section-title">General</div>
            <Row className="g-3">
              <Col md={6}>{textField("Dashboard Name", "dashboard_name")}</Col>
              <Col md={6}>
                {textField("Dashboard Category", "dashboard_category")}
              </Col>
              <Col md={6}>
                {textField("Dashboard Status", "dashboard_status")}
              </Col>
            </Row>

            <div className="pv-section-title">Data Source</div>
            <Row className="g-3">
              <Col md={6}>{textField("Data Reference", "data_reference")}</Col>
              <Col md={6}>
                {textField("Data Reference Type", "data_reference_type")}
              </Col>
            </Row>

            <div className="pv-section-title">Configuration</div>
            <Form.Group className="ep-group">
              <Form.Label className="pv-label">Dashboard Details (JSON)</Form.Label>
              <Form.Control
                as="textarea"
                className="ep-prompt"
                style={{ minHeight: 140 }}
                name="details_object"
                value={detailsText}
                onChange={handleDetailsChange}
                disabled={!editable}
                spellCheck={false}
              />
            </Form.Group>
            <div className="ep-note">
              Add <code>"dashboard_description"</code> to show an About button
              beside the dashboard title, or set{" "}
              <code>"enable_price_overlay": true</code> to offer a single-line
              price overlay when dashboard data includes{" "}
              <code>price_ticker</code>.
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="ep-footer">
          {editable ? (
            <>
              {!isCreatingNewDashboard && (
                <Button
                  variant="outline-danger"
                  className="me-auto"
                  onClick={confirmDelete}
                >
                  Delete
                </Button>
              )}
              <Button variant="outline-secondary" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSave}>
                {isCreatingNewDashboard ? "Create Dashboard" : "Save Changes"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline-secondary" onClick={handleCloseModal}>
                Close
              </Button>
              <Button variant="primary" onClick={() => setEditable(true)}>
                Edit
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered>
        <Modal.Header closeButton className="pv-header">
          <div>
            <div className="pv-eyebrow">Confirm</div>
            <div className="pv-title up-title">Delete dashboard?</div>
          </div>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to permanently delete{" "}
          <strong>{selectedDashboard?.dashboard_name}</strong>? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer className="ep-footer">
          <Button variant="outline-secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Modal */}
      <Modal show={showSuccessModal} onHide={handleCloseSuccessModal} centered>
        <Modal.Header closeButton className="pv-header">
          <div>
            <div className="pv-eyebrow">Done</div>
            <div className="pv-title up-title">Changes saved</div>
          </div>
        </Modal.Header>
        <Modal.Body>Your changes have been saved successfully.</Modal.Body>
        <Modal.Footer className="ep-footer">
          <Button variant="primary" onClick={handleCloseSuccessModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default DashboardDetails;
